/** Проблемы поставки: чипсы видов, список разбора, сворачиваемый блок «что не так» для дерева. */
import React, { useState } from "react";

import type { SupplyIssue } from "@/api/ozonAnalytics";
import { cn } from "@/lib/utils";
import { rub } from "./format";
import { issueKinds, issueLabel, sevClass } from "./issues";
import { useSupplyDetail } from "./queries";
import { ErrorBox } from "./shared";

export function IssueChips({ issues }: { issues: SupplyIssue[] }): React.ReactElement {
  const kinds = issueKinds(issues);
  if (!kinds.length) {
    return (
      <span className="rounded-full bg-emerald-100 px-1.5 py-px text-[11px] text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">
        без замечаний
      </span>
    );
  }
  return (
    <span className="inline-flex flex-wrap gap-1">
      {kinds.map((k) => (
        <span
          key={k.kind}
          className={cn("whitespace-nowrap rounded-full px-1.5 py-px text-[11px]", sevClass(k.sev))}
          title={k.sev}
        >
          {issueLabel(k.kind)}
          {k.count > 1 && ` ×${k.count}`}
        </span>
      ))}
    </span>
  );
}

export function IssueList({ issues }: { issues: SupplyIssue[] }): React.ReactElement {
  if (!issues.length) {
    return <p className="text-xs text-muted-foreground">Разбор по актам замечаний не нашёл.</p>;
  }
  return (
    <ul className="space-y-1 text-[13px]">
      {issues.map((i, n) => (
        <li key={n} className="flex gap-2">
          <span
            className={cn(
              "mt-0.5 h-fit shrink-0 whitespace-nowrap rounded-full px-1.5 py-px text-[11px]",
              sevClass(i.sev),
            )}
          >
            {issueLabel(i.kind)}
          </span>
          <span>
            {i.text}
            {i.amount !== null && i.amount !== undefined && Math.abs(i.amount) >= 0.5 && (
              <span className="ml-1 tabular-nums text-muted-foreground">({rub(i.amount)})</span>
            )}
            {i.supply_id !== undefined && i.supply_id !== null && (
              <span className="ml-1 text-xs text-muted-foreground">· поставка {i.supply_id}</span>
            )}
          </span>
        </li>
      ))}
    </ul>
  );
}

/** Сворачиваемый «что не так с поставкой» в строке дерева: разбор грузится при раскрытии. */
export function SupplyIssuesDetails({
  orderNumber,
  legal,
  alloc,
}: {
  orderNumber: string;
  legal: string | null;
  alloc: boolean;
}): React.ReactElement {
  const [open, setOpen] = useState(false);
  const q = useSupplyDetail(orderNumber, legal, alloc, open);
  const issues = q.data?.supplies.flatMap((s) =>
    (s.issues ?? []).map((i) => ({ ...i, supply_id: s.supply_id })),
  );
  return (
    <details
      className="mt-1 text-xs"
      onClick={(e) => e.stopPropagation()}
      onToggle={(e) => setOpen(e.currentTarget.open)}
    >
      <summary className="cursor-pointer text-primary">Что не так с поставкой</summary>
      <div className="mt-1 max-w-3xl whitespace-normal">
        {q.isLoading && <span className="text-muted-foreground">Загружаю разбор…</span>}
        {q.isError && <ErrorBox error={q.error} what="разбор поставки" />}
        {q.data && q.data.supplies.length === 0 && (
          <span className="text-muted-foreground">
            Диагноза по заявке нет (в ozon_fin_supply_diag её нет) — есть только начисления.
          </span>
        )}
        {issues && q.data && q.data.supplies.length > 0 && <IssueList issues={issues} />}
      </div>
    </details>
  );
}
